import { ArrowLeft, Loader2, Play } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { useEvalCaseQuery } from '../hooks/useEvalCaseQuery'
import { useEvalCaseAssertionsQuery } from '../hooks/useEvalCaseAssertionsQuery'
import { useStartEvalCaseRunsMutation } from '../hooks/useStartEvalCaseRunMutation'
import { EvalDetailStatePanel } from './EvalDetailStatePanel'
import { EvalPromptCard } from './EvalCaseDetail/EvalPromptCard'
import { EvalAssertionsCard } from './EvalCaseDetail/EvalAssertionsCard'
import { EvalRunsCard } from './EvalCaseDetail/EvalRunsCard'
import { EvalModelCard } from './EvalCaseDetail/EvalModelCard'
import { EvalCardSkeleton } from './EvalCaseDetail/EvalCardSkeleton'
import { EditableCaseName } from './EvalCaseDetail/EditableCaseName'

interface EvalCaseDetailPageProps {
  dutyId: string
  caseId: string
}

export function EvalCaseDetailPage({
  dutyId,
  caseId,
}: EvalCaseDetailPageProps) {
  const { toTask } = useAppNavigate()
  const caseQuery = useEvalCaseQuery(caseId)
  const assertionsQuery = useEvalCaseAssertionsQuery(caseId)
  const startRun = useStartEvalCaseRunsMutation()
  const evalCase = caseQuery.data ?? null
  const hasAssertions = (assertionsQuery.data?.items ?? []).some(
    (row) => row.active
  )
  const isLoading = !evalCase && (caseQuery.isLoading || caseQuery.isPending)

  const handleBack = () =>
    toTask(`/evals/${dutyId}/test-cases`, { replace: false })

  const handleRun = () => {
    startRun.mutate([caseId], {
      onSuccess: () => toast.success('Queued a run for this test case.'),
      onError: (error: Error) =>
        toast.error(error.message || 'Unable to queue a run.'),
    })
  }

  if (!evalCase && caseQuery.isError) {
    return (
      <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
        <EvalDetailStatePanel
          state="error"
          label="Unable to load test case"
          heading
          className="p-6"
        />
      </div>
    )
  }

  if (!evalCase && !isLoading) {
    return (
      <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
        <EvalDetailStatePanel
          state="not-found"
          label="Test case not found"
          heading
          className="p-6"
        />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-y-auto">
      <header className="flex shrink-0 items-center justify-between gap-2 p-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-1.5 text-sm font-medium text-primary-high-contrast">
          <ArrowLeft className="size-[18px]" />
          Test Cases
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="default"
          className="h-9 rounded-xl px-3.5 text-sm font-semibold"
          disabled={startRun.isPending}
          onClick={handleRun}>
          {startRun.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Play className="size-4" />
          )}
          Run
        </Button>
      </header>

      <div className="mx-auto flex w-full max-w-[calc(75rem)] flex-col gap-4 px-4 pb-4">
        <div className="min-w-0">
          {isLoading || !evalCase ? (
            <Skeleton className="h-7 w-72" />
          ) : (
            <EditableCaseName caseId={caseId} name={evalCase.name} />
          )}
        </div>

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(20rem,1fr)]">
          {isLoading || !evalCase ? (
            <>
              <span className="sr-only" role="status">
                Loading test case...
              </span>
              <div className="flex min-w-0 flex-col gap-4">
                <EvalCardSkeleton lines={5} />
                <EvalCardSkeleton lines={3} />
                <EvalCardSkeleton lines={4} />
              </div>
              <div className="flex min-w-0 flex-col gap-4">
                <EvalCardSkeleton lines={2} />
              </div>
            </>
          ) : (
            <>
              <div className="flex min-w-0 flex-col gap-4">
                <EvalPromptCard evalCase={evalCase} />
                <EvalAssertionsCard caseId={caseId} />
                <EvalRunsCard caseId={caseId} hasAssertions={hasAssertions} />
              </div>
              <div className="flex min-w-0 flex-col gap-4">
                <EvalModelCard evalCase={evalCase} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
